import { useCallback } from 'react';
import { useEvent } from 'effector-react';
import { useNavigateControlbar, useNavigateMenuItem } from '@nx-frontend/shared/hooks';
import { navigateEvent, selectedWidgetEvent } from '@nx-frontend/shared/store/customizer-controlbar-store';
import { getIsActiveMenuItemIdFx, toggleExpandItemFx } from '@nx-frontend/shared/store/feature-menu-tree';
import {
  ControlbarEditEntityType,
  MenuItem,
  MenuItemType,
  MenuType,
  PortalEditWidgetType,
  ScreenType,
} from '@nx-frontend/shared/types';
import { isAbsoluteUrl } from '@nx-frontend/shared/utils';

export const useOpenPortalMenuItem = () => {
  const [toggleExpandItem, getIsActiveMenuItemId, navigate] = useEvent([
    toggleExpandItemFx,
    getIsActiveMenuItemIdFx,
    navigateEvent,
  ]);
  const navigateMenuItem = useNavigateMenuItem();
  const changeActiveWidget = useEvent(selectedWidgetEvent);
  const navigateControlbar = useNavigateControlbar();
  return useCallback(
    async (item: MenuItem, menuType: MenuType) => {
      if (item.url) {
        if (item.type === MenuItemType.LINK || isAbsoluteUrl(item.url)) {
          navigateControlbar({ entityType: ControlbarEditEntityType.LINK, entityId: item.id });
        } else {
          navigateControlbar({ entityType: null });
          if (item.type === MenuItemType.NOTES_FOLDER || item.type === MenuItemType.PORTAL_SECTION) {
            const isExpanded = await getIsActiveMenuItemId({ itemId: item.id });
            if (!isExpanded) {
              toggleExpandItem({ item, menuType });
            }
          }
          navigateMenuItem({ path: item.url, originalPath: item.originalUrl });
          navigate({
            screen: { type: ScreenType.MAIN },
          });
        }
      }
      changeActiveWidget({ selectedWidget: getPortalWidgetTypeFromMenuType(menuType) });
    },
    [changeActiveWidget, getIsActiveMenuItemId, navigate, navigateControlbar, navigateMenuItem, toggleExpandItem]
  );
};

function getPortalWidgetTypeFromMenuType(menuType: MenuType): PortalEditWidgetType {
  switch (menuType) {
    case MenuType.PORTAL_SIDEBAR:
      return PortalEditWidgetType.SIDEBAR;
    case MenuType.PORTAL_TOPBAR:
      return PortalEditWidgetType.TOPBAR;
    case MenuType.PORTAL_FOOTER:
      return PortalEditWidgetType.FOOTER;
    default:
      throw new Error('Unknown type');
  }
}
